import type { ReactNode } from "react";
import { Link } from "react-router";
import { useProduct } from "../lib/useProduct.js";

/**
 * 利用規約・プライバシーポリシー・特商法表記など、ログイン不要で読める文書の共通レイアウト。
 * 文面はデータ（LegalArticle[]）で渡す。番号つきの項は <ol>、枝番（1）（2）は入れ子の ol。
 */
export type OlItem = string | { text: string; sub: string[] };
export type LegalNode = { p: string } | { ol: OlItem[] } | { jsx: ReactNode };
export type LegalArticle = { heading: string; nodes: LegalNode[] };

/** メールアドレスをソースにそのまま出さない（収集ボット対策）。表示の瞬間に "user"＋"@"＋"domain" を組み立てる */
export function Email({ user, domain, label }: { user: string; domain: string; label?: string }) {
  const addr = user + String.fromCharCode(64) + domain;
  return (
    <a href={"mailto:" + addr}>
      {label ?? addr}
    </a>
  );
}

function Nodes({ nodes }: { nodes: LegalNode[] }) {
  return (
    <>
      {nodes.map((n, i) =>
        "p" in n ? (
          <p key={i}>{n.p}</p>
        ) : "jsx" in n ? (
          <div key={i} className="legal-jsx">
            {n.jsx}
          </div>
        ) : (
          <ol key={i}>
            {n.ol.map((it, j) =>
              typeof it === "string" ? (
                <li key={j}>{it}</li>
              ) : (
                <li key={j}>
                  {it.text}
                  <ol className="sub">
                    {it.sub.map((s, k) => (
                      <li key={k}>{s}</li>
                    ))}
                  </ol>
                </li>
              ),
            )}
          </ol>
        ),
      )}
    </>
  );
}

export function LegalDoc({
  title,
  intro,
  articles,
  updated,
}: {
  title: string;
  intro?: string;
  articles: LegalArticle[];
  updated: string;
}) {
  const P = useProduct();
  return (
    <main className="legal" data-product={P.product}>
      <header className="legal-top">
        <Link to="/login" className="legal-logo" aria-label={P.name}>
          <img src={P.assets.logo} alt={P.name} width={324} height={100} />
        </Link>
      </header>
      <article className="legal-doc">
        <h1>{title}</h1>
        {intro ? <p className="legal-intro">{intro}</p> : null}
        {articles.map((a, i) => (
          <section key={i}>
            <h2>{a.heading}</h2>
            <Nodes nodes={a.nodes} />
          </section>
        ))}
        <p className="legal-updated">{updated}</p>
      </article>
    </main>
  );
}
